import { FileDownload } from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormHelperText,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { RandomTextSkeleton } from "../../../../App/Components/Skeleton/RandomTextSkeleton";
import { useFileUploadStore } from "../State/useFileUploadStore";
import {
  useCandidateAttachments,
  useUploadCandidateAttachmentMutation,
} from "../../../../App/Queries/CandidateQueries";
import { useCallback, useContext, useEffect, useRef } from "react";
import { CandidateContext } from "../../../CandidateProfile";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";

export const FileUploads = () => {
  return (
    <Box>
      <UploadFileButton />
      <Divider sx={{ my: 2 }} />
      <AttachmentDownloadList />
    </Box>
  );
};

const UploadFileButton = () => {
  const { candidate } = useContext(CandidateContext);
  const { hasFileError, hasUploadError, errorMessage, validateFile, setUploadError, clearUpload } =
    useFileUploadStore();
  const { mutateAsync, isLoading } = useUploadCandidateAttachmentMutation();
  const { displayError, displaySuccess } = useNotification();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const errorCooldown = useRef<ReturnType<typeof setTimeout>>();
  const errorFile = useRef<File | null>();

  const handleClearUpload = useCallback(() => {
    clearUpload();
    if (fileInput.current) {
      fileInput.current.value = "";
    }
    if (errorFile.current) {
      errorFile.current = null;
    }
    if (errorCooldown.current) {
      clearTimeout(errorCooldown.current);
    }
  }, [clearUpload]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileUploaded = e.currentTarget.files && e.currentTarget.files[0];
    handleClearUpload();
    if (fileUploaded && validateFile(fileUploaded)) {
      await handleSubmitFile(fileUploaded);
    } else {
      errorFile.current = fileUploaded;
    }
  };

  const handleSubmitFile = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      await mutateAsync(
        {
          file: formData,
          fileName: file.name,
          candidateId: candidate.id,
        },
        {
          onError: () => {
            const message = "An error occurred while uploading your file. Please try again.";
            setUploadError(true, message);
            displayError(message);
          },
          onSettled: () => {
            handleClearUpload();
            displaySuccess("File uploaded successfully");
          },
        },
      );
    },
    [mutateAsync, candidate.id, setUploadError, displayError, handleClearUpload, displaySuccess],
  );

  useEffect(() => {
    if (hasFileError) {
      errorCooldown.current = setTimeout(() => {
        handleClearUpload();
      }, 5000);
    }
    return () => {
      clearTimeout(errorCooldown.current);
    };
  }, [hasFileError, handleClearUpload]);

  return (
    <>
      <Button
        role={undefined}
        tabIndex={-1}
        variant="outlined"
        component="label"
        htmlFor="file-upload"
        color={hasFileError || hasUploadError ? "error" : "secondary"}
        data-test="file-upload-button-label"
        onClick={handleClearUpload}
        disabled={isLoading}
      >
        Upload Attachment
        <VisuallyHiddenInput
          ref={fileInput}
          id="file-upload"
          type="file"
          onChange={!isLoading ? handleFileChange : undefined}
          data-test="file-upload-input"
        />
      </Button>
      <Collapse in={errorMessage.length > 0} unmountOnExit>
        {errorFile.current !== null && (
          <Typography variant="caption" data-test="file-error-name">
            File name: <strong>{errorFile.current?.name}</strong>
          </Typography>
        )}
        <FormHelperText
          sx={(theme) => ({ mt: 1, color: theme.palette.error.main })}
          data-test="file-upload-error"
        >
          {errorMessage}
        </FormHelperText>
      </Collapse>
    </>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const AttachmentDownloadList = () => {
  const { candidate } = useContext(CandidateContext);
  const { data: attachments = [], isLoading } = useCandidateAttachments(candidate.id);
  return (
    <Stack component="ul" direction="column" alignItems="flex-start" sx={{ padding: 0, margin: 0 }}>
      {isLoading ? (
        <RandomTextSkeleton />
      ) : (
        <>
          {attachments.length > 0 ? (
            attachments.map((attachment) => (
              <Tooltip
                key={attachment.id}
                placement="right"
                arrow
                title={attachment.id === "temp" ? "File uploading..." : `Download ${attachment.name}`}
              >
                <Box component="li" sx={{ padding: 0, margin: 0, listStyle: "none" }}>
                  <Button
                    key={attachment.id}
                    component="a"
                    href={attachment.fileUrl}
                    data-test={`candidate-fileupload-${attachment.name}`}
                    download={attachment.name}
                    target="_blank"
                    variant="text"
                    endIcon={<FileDownload />}
                    disabled={attachment.id === "temp"}
                  >
                    {attachment.name}
                  </Button>
                </Box>
              </Tooltip>
            ))
          ) : (
            <Typography component="li" sx={{ padding: 0, margin: 0, listStyle: "none" }}>
              No attachments found
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};
